import { css } from '@compiled/react';
import React, { useCallback, useMemo, Fragment } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import { ValidationContextProvider } from '@atlaskit/renderer';
import { token } from '@atlaskit/tokens';

import { ADFRendererWithExtensions } from '@confluence/adf-renderer/entry-points/ADFRendererWithExtensions.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { i18n } from './messages.i18n.ts';

const activeWrapper = css({
	overflowX: 'scroll',
});

const header = css({
	backgroundColor: token('color.background.neutral'),
	borderBottom: `1px solid ${token('color.border')}`,
	paddingTop: token('space.150'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.100'),
});

const rendererWrapper = css({
	paddingLeft: token('space.400'),
	paddingRight: token('space.400'),
});

export const ReadOnlyLCM = ({
	document,
	children,
	contentId,
	isLiveViewMode,
}: {
	document: any;
	children: React.ReactNode;
	contentId?: string;
	isLiveViewMode?: boolean;
}) => {
	const [editable, setEditable] = React.useState(false);
	const spaceId = useSpaceId();
	const spaceKey = useSpaceKey();

	React.useEffect(() => {
		function onAnotherLCMEditable() {
			setEditable(false);
		}
		window.addEventListener('LCMMadeEditable', onAnotherLCMEditable);

		return () => {
			window.removeEventListener('LCMMadeEditable', onAnotherLCMEditable);
		};
	}, [setEditable]);

	const makeEdits = useCallback(() => {
		window.dispatchEvent(new CustomEvent('LCMMadeEditable'));
		setEditable(true);
	}, []);

	const validationContextValue = useMemo(() => ({ skipValidation: true }), []);

	if (!editable) {
		return (
			<Fragment>
				<Header setEditable={makeEdits} editable={editable} isLiveViewMode={isLiveViewMode} />
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives */}
				<div css={rendererWrapper}>
					<ValidationContextProvider value={validationContextValue}>
						<ADFRendererWithExtensions
							appearance="full-width"
							contentId={contentId}
							document={document}
							spaceId={spaceId}
							spaceKey={spaceKey || ''}
						/>
					</ValidationContextProvider>
				</div>
			</Fragment>
		);
	}

	return (
		<Fragment>
			<Header setEditable={makeEdits} editable={editable} />
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives */}
			<div css={activeWrapper}>{children}</div>
		</Fragment>
	);
};

function Header({
	setEditable,
	editable,
	isLiveViewMode,
}: {
	setEditable: () => void;
	editable: boolean;
	isLiveViewMode?: boolean;
}) {
	const learnMore = useCallback(() => {
		window.open(
			'https://support.atlassian.com/confluence-cloud/docs/the-legacy-content-macro/',
			'_blank',
		);
	}, []);
	const { formatMessage } = useIntl();
	return (
		<div css={header}>
			<Inline spread="space-between" alignBlock="center">
				<Text color="color.text.subtle" size="small">
					{formatMessage(i18n.contentHasLimitedFunctionality, {
						strong: (chunks: React.ReactNode[]) => {
							return <Text weight="bold">{chunks}</Text>;
						},
						link: (chunks: React.ReactNode[]) => {
							return (
								<Link
									onClick={learnMore}
									href="https://support.atlassian.com/confluence-cloud/docs/the-legacy-content-macro/"
									target="_blank"
								>
									{chunks}
								</Link>
							);
						},
					})}{' '}
				</Text>
				{editable === false &&
				!(
					expValEquals('platform_editor_disable_view_mode_lcm_editing', 'isEnabled', true) &&
					isLiveViewMode
				) ? (
					<Button spacing="compact" onClick={setEditable} iconBefore={EditIcon}>
						<FormattedMessage {...i18n.makeEdits} />
					</Button>
				) : null}
			</Inline>
		</div>
	);
}
